import { IAction } from "~/components/shared/add-action-modal/types";
import { routes } from "~/lib/constants";

export interface ActionButtonDef {
  label: string;
  iconName?: string; // name of the lucide-react icon, e.g., "Clock", "Eye"
  variant: "primary" | "secondary" | "outline" | "ghost" | "danger" | "emerald";
  actionType: "navigate" | "navigate_new_tab" | "modal" | "custom";
  route?: string; // used if actionType is "navigate" or "navigate_new_tab"
  modalType?:
    | "fillWorkHours"
    | "viewWorkHours"
    | "viewAuditPdf"
    | "viewInspectionPdf"
    | "workspaceAction"; // used if actionType is "modal"
  onClick?: (action: IAction) => void; // generic handler if needed
}

export interface ActionRegistryEntry {
  value: string;
  label: string;
  allowedModels: string[];
  useStandardCreation: boolean;
  getButtons: (
    action: IAction,
    context: {
      isAssignedToMe: boolean;
      isNotCompleted: boolean;
      hasFullAccess: boolean;
    },
  ) => ActionButtonDef[];
}

export const ACTION_REGISTRY: Record<string, ActionRegistryEntry> = {
  LogHours: {
    value: "LogHours",
    label: "Log Hours",
    allowedModels: ["WorkHoursRegister"],
    useStandardCreation: false,
    getButtons: (
      _action,
      { isAssignedToMe, isNotCompleted, hasFullAccess },
    ) => {
      const buttons: ActionButtonDef[] = [];
      if (isAssignedToMe && isNotCompleted) {
        buttons.push({
          label: "Log Hours",
          iconName: "Clock",
          variant: "emerald",
          actionType: "modal",
          modalType: "fillWorkHours",
        });
      } else if (hasFullAccess || !isNotCompleted) {
        buttons.push({
          label: "View Hours",
          iconName: "Eye",
          variant: "outline",
          actionType: "modal",
          modalType: "viewWorkHours",
        });
      }
      return buttons;
    },
  },
  CorrectiveAction: {
    value: "CorrectiveAction",
    label: "Corrective Action",
    allowedModels: [
      "Incident",
      "Observation",
      "Audit",
      "Inspection",
      "Finding",
      "MOC",
      "Asset",
    ],
    useStandardCreation: true,
    getButtons: (_action, { isAssignedToMe, isNotCompleted }) => {
      const buttons: ActionButtonDef[] = [];
      if (isAssignedToMe && isNotCompleted) {
        buttons.push({
          label: "Resolve",
          iconName: "ClipboardCheck",
          variant: "emerald",
          actionType: "modal",
          modalType: "workspaceAction",
        });
      } else {
        buttons.push({
          label: "View",
          iconName: "Eye",
          variant: "outline",
          actionType: "modal",
          modalType: "workspaceAction",
        });
      }
      return buttons;
    },
  },
  AuditTask: {
    value: "AuditTask",
    label: "Audit Task",
    allowedModels: ["Audit"],
    useStandardCreation: true,
    getButtons: (action, { isAssignedToMe, isNotCompleted }) => {
      const isFilled =
        action.model?.status === "Ready for Review" ||
        action.model?.status === "Closed";
      const buttons: ActionButtonDef[] = [];
      const auditId =
        action.model?.id ||
        (action as any).modelId ||
        (action as any).auditId ||
        "";
      if (isAssignedToMe && isNotCompleted && !isFilled) {
        buttons.push({
          label: "Fill Audit",
          iconName: "ClipboardCheck",
          variant: "emerald",
          actionType: "navigate_new_tab",
          route: routes.completeAudit({ id: auditId }),
        });
      } else {
        buttons.push({
          label: "View Audit",
          iconName: "Eye",
          variant: "outline",
          actionType: "modal",
          modalType: "viewAuditPdf",
        });
      }
      return buttons;
    },
  },
  InspectionTask: {
    value: "InspectionTask",
    label: "Inspection Task",
    allowedModels: ["Inspection"],
    useStandardCreation: true,
    getButtons: (action, { isAssignedToMe, isNotCompleted }) => {
      const isFilled =
        action.model?.status === "Ready for Review" ||
        action.model?.status === "Closed";
      const buttons: ActionButtonDef[] = [];
      const inspectionId =
        action.model?.id ||
        (action as any).modelId ||
        (action as any).inspectionId ||
        "";
      if (isAssignedToMe && isNotCompleted && !isFilled) {
        buttons.push({
          label: "Fill Inspection",
          iconName: "ClipboardCheck",
          variant: "emerald",
          actionType: "navigate_new_tab",
          route: routes.completeInspection({ id: inspectionId }),
        });
      } else {
        buttons.push({
          label: "View Inspection",
          iconName: "Eye",
          variant: "outline",
          actionType: "modal",
          modalType: "viewInspectionPdf",
        });
      }
      return buttons;
    },
  },
};
